import React from "react";
import { formatDateTime } from "../utils/actionUtils";
import { BackIcon } from "./icons";
import {
  formatRequestValue,
  getRequestActionLabel,
  getRequestChangedFields,
  getRequestEntityLabel,
  getRequestTargetName,
} from "../../utils/mutationRequestDisplayUtils";

const titleCase = (value: string) =>
  String(value || "")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const statusLabel = (request: any) =>
  titleCase(String(request?.status || "PENDING").toLowerCase());

const isBankAccountRequest = (request: any) =>
  String(request?.entityType || "").toUpperCase() === "BANK_ACCOUNT";

const hasValue = (value: any) =>
  value !== null && value !== undefined && value !== "";

const getChangeDisplayValue = (change: any) => {
  const hasOld = hasValue(change?.oldValue);
  const hasNew = hasValue(change?.newValue);

  if (!hasOld && !hasNew) return null;

  const oldValue = hasOld ? formatRequestValue(change.oldValue, change.field) : null;
  const newValue = hasNew ? formatRequestValue(change.newValue, change.field) : null;
  const oldDisplay = oldValue && oldValue !== "Not available" ? oldValue : null;
  const newDisplay = newValue && newValue !== "Not available" ? newValue : null;

  if (!oldDisplay && !newDisplay) return null;
  if (oldDisplay && newDisplay && oldDisplay === newDisplay) return null;
  if (oldDisplay && newDisplay) return `${oldDisplay} -> ${newDisplay}`;

  return newDisplay || oldDisplay;
};

const RequestDetailView = ({
  t,
  request,
  childrenList = [],
  families = [],
  parentData = null,
  onBack,
}: any) => {
  const bankAccountRequest = isBankAccountRequest(request);
  const entityLabel = bankAccountRequest
    ? t("finance.registered_account", { defaultValue: "Bank account" })
    : getRequestEntityLabel(request);
  const actionLabel = bankAccountRequest
    ? t("parentPortal.actions.bank_change_action", {
        defaultValue: "Administrative update",
      })
    : getRequestActionLabel(request);
  const targetName = getRequestTargetName(request, childrenList, families, parentData);
  const changedFields = getRequestChangedFields(request)
    .map((change: any) => ({
      ...change,
      displayValue: getChangeDisplayValue(change),
    }))
    .filter((change: any) => change.displayValue);
  const requestedAt =
    formatDateTime(request?.createdAt || request?.requestedAt || request?.submittedAt) ||
    "";
  const status = bankAccountRequest
    ? t("parentPortal.bank_change_status", { defaultValue: "Change received" })
    : statusLabel(request);

  return (
    <div className="pp-actions-app-view pp-info-detail-page pp-family-view pp-request-detail-view">
      <button type="button" className="pp-info-back-button" onClick={onBack}>
        <BackIcon aria-hidden="true" />
        {t("common.back", { defaultValue: "Back" })}
      </button>

      <header className="pp-request-detail-hero">
        <span className="pp-request-status-pill">{status}</span>
        <h2>
          {t("parentPortal.requests.detail_title", {
            defaultValue: bankAccountRequest
              ? "Administrative change details"
              : "Request details",
          })}
        </h2>
        <p>{targetName}</p>
      </header>

      <section className="pp-info-detail-card pp-request-detail-card">
        <div className="pp-request-summary">
          <span>{entityLabel}</span>
          <strong>{actionLabel}</strong>
          {requestedAt && <small>{requestedAt}</small>}
        </div>

        {changedFields.length > 0 ? (
          <div className="pp-request-detail-list">
            {changedFields.map((change: any, index: number) => (
              <div className="pp-request-detail-row" key={`${change.field}-${index}`}>
                <span className="pp-request-detail-label">{titleCase(change.field)}</span>
                <span className="pp-request-detail-value">{change.displayValue}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="pp-request-detail-empty">
            {t("parentPortal.requests.no_payload_details", {
              defaultValue: "No readable change details are available for this request.",
            })}
          </p>
        )}
      </section>
    </div>
  );
};

export default RequestDetailView;
